import React, {
  PropsWithChildren,
  useCallback,
  useEffect,
  useState,
} from 'react'

import { useQuery } from '@apollo/client'

import { PlanType } from '$extensionSrc/__generated__/graphql'
import ActionButton, {
  ActionButtonType,
} from '$extensionSrc/components/ActionButton'
import InteractiveElement from '$extensionSrc/components/InteractiveElement'
import Toast from '$extensionSrc/components/Toast'
import {
  enterprisePlanRedeemCodePath,
  manageBillingPath,
  selectPlanPath,
} from '$extensionSrc/consts/urlConsts'
import { User } from '$extensionSrc/data/user/user'
import { formatMonthDayAndYear } from '$extensionSrc/utils/dateUtils'
import { UserSettingsModalType } from '$extensionSrc/utils/enums'
import { authFetch } from '$serverSrc/utils/fetch'

import SettingsModal from './SettingsModal'
import UserSettingsCredentialRow, {
  UserSettingsCredentialSpec,
} from './UserSettingsCredentialRow'
import UserSettingsPaidPlanRow, {
  SubscriptionDetails,
} from './UserSettingsPaidPlanRow'
import UserSettingsRow from './UserSettingsRow'
import { client } from '../apolloClient'
import { userQuery } from '../data/query'

import './UserSettingsPage.scss'

export type EnterpriseDetails = {
  hasEnterprisePlan: boolean
  hasEnterpriseEmailDomain: boolean
  allowAddCredential: boolean
}

// TODO: Confirm email button?

function UserSettingsSection({
  title,
  children,
}: PropsWithChildren<{
  title: string
}>) {
  return (
    <div className="UserSettingsSection">
      <h3 className="subheading3">{title}</h3>
      <div className="section-items">{children}</div>
    </div>
  )
}

export default function UserSettingsPage({
  currentUserId,
  currentUserNameDisplay,
  currentUserPrimaryEmail,
  showPlanSection,
  credentialSpecs,
  enterpriseDetails,
  subscriptionDetails,
}: {
  currentUserId: number
  currentUserPrimaryEmail: string | null
  currentUserNameDisplay: string | null
  credentialSpecs: UserSettingsCredentialSpec[]
  showPlanSection: boolean
  enterpriseDetails: EnterpriseDetails
  subscriptionDetails: SubscriptionDetails | null
}) {
  const [currentModalType, setCurrentModalType] = useState<string | null>(null)

  const [updatedCurrentUserNameDisplay, setUpdatedCurrentUserNameDisplay] =
    useState(currentUserNameDisplay)

  const [primaryEmail, setPrimaryEmail] = useState(currentUserPrimaryEmail)
  const availableEmails = credentialSpecs
    .filter((x) => x.emailConfirmed)
    .map((x) => x.email)
  const showChangeEmailButton =
    credentialSpecs.length > 1 && availableEmails.length > 1

  // Grab user data
  const {
    loading: userDataLoading,
    error: userDataError,
    data: userData,
  } = useQuery(userQuery, {
    client,
    variables: {
      id: currentUserId,
    },
    onCompleted: (data) => {
      setUpdatedCurrentUserNameDisplay(data?.user?.name)
    },
  })

  const currentUser: Partial<User> = userData?.user || {}

  const [phoneNumber, setPhoneNumber] = useState(null)

  const [toastInfo, setToastInfo] = useState({
    show: false,
    text: '',
    error: false,
  })

  useEffect(() => {
    if (currentModalType) {
      setToastInfo({
        show: false,
        text: '',
        error: false,
      })
    }
  }, [currentModalType, setToastInfo])

  const handleUpgradeClick = useCallback(() => {
    let pathname = selectPlanPath
    try {
      const url = new URL(window.location.href)
      const cfIpcountry = url.searchParams.get('cf_ipcountry')
      if (cfIpcountry) {
        const usp = new URLSearchParams({
          cf_ipcountry: cfIpcountry,
        })
        pathname = `${pathname}?${usp.toString()}`
      }
      // eslint-disable-next-line no-empty
    } catch {}
    window.location.assign(pathname)
  }, [])

  const handleClickAddAuthenticationMethod = useCallback(() => {
    window.location.assign('/add-authentication-method/?next=%2Fsettings%2F')
  }, [])

  const handleClickManageSubscription = useCallback(async () => {
    let pathname
    try {
      const request = new Request(manageBillingPath, { method: 'POST' })
      const response = await authFetch(request)
      const data = await response.json()
      pathname = data.url
      window.location.assign(pathname)
      // eslint-disable-next-line no-empty
    } catch {}
  }, [])

  const handleClickActivateEnterprisePlan = useCallback(() => {
    window.location.assign(enterprisePlanRedeemCodePath)
  }, [])

  if (userDataLoading) {
    return null
  }
  return (
    <div className="UserSettingsPage">
      <div className="heading1 bold center w-100">Account Settings</div>
      <div className="user-settings-body">
        {/* Profile information */}
        <UserSettingsSection title="Profile">
          <UserSettingsRow>
            <div className="d-flex flex-column">
              <div className="body1 bold">Name</div>
              <div className="body1">
                {updatedCurrentUserNameDisplay || 'None'}
              </div>
            </div>
            <ActionButton
              className="edit-button"
              onClick={() =>
                setCurrentModalType(UserSettingsModalType.CHANGE_NAME)
              }
              type={ActionButtonType.SECONDARY}
            >
              Edit
            </ActionButton>
          </UserSettingsRow>
          <UserSettingsRow>
            <div className="d-flex flex-column">
              <div className="body1 bold">Email</div>
              <div className="body1">{primaryEmail}</div>
            </div>
            {showChangeEmailButton && (
              <ActionButton
                className="edit-button"
                onClick={() =>
                  setCurrentModalType(UserSettingsModalType.CHANGE_EMAIL)
                }
                type={ActionButtonType.SECONDARY}
              >
                Change
              </ActionButton>
            )}
          </UserSettingsRow>
        </UserSettingsSection>
        {/* Plan information */}
        {showPlanSection && (
          <UserSettingsSection title="Plan">
            <UserSettingsPaidPlanRow
              onUpgradeClick={handleUpgradeClick}
              onClickManageSubscription={handleClickManageSubscription}
              planExpiresOn={currentUser.planExpiresOn || null}
              planType={currentUser.planType || PlanType.NoPlan}
              trialExpiresOn={currentUser.trialExpiresOn || null}
              hasEnterprisePlan={enterpriseDetails.hasEnterprisePlan}
              subscriptionDetails={subscriptionDetails}
            />
            {enterpriseDetails.hasEnterpriseEmailDomain &&
              currentUser.planType !== PlanType.StandardPlan && (
                <UserSettingsRow className="action-row">
                  <div className="body2">
                    Have a license code from your employer? Activate your
                    employer-sponsored access.
                  </div>
                  <ActionButton
                    className="edit-button"
                    onClick={handleClickActivateEnterprisePlan}
                    type={ActionButtonType.SECONDARY}
                  >
                    Activate
                  </ActionButton>
                </UserSettingsRow>
              )}
          </UserSettingsSection>
        )}
        {/* Account credentials information */}
        <UserSettingsSection title="Login">
          {credentialSpecs.map((spec) => (
            <UserSettingsCredentialRow
              credentialType={spec.credentialType}
              email={spec.email}
              emailConfirmed={spec.emailConfirmed}
              handleRemove2fa={() =>
                setCurrentModalType(UserSettingsModalType.DELETE_2FA)
              }
              key={spec.email}
              twofaMethod={spec.twofaMethod}
              twofaPhoneNumber={spec.twofaPhoneNumber}
              openIdIssuer={spec.openIdIssuer}
            />
          ))}
          {enterpriseDetails.allowAddCredential && (
            <UserSettingsRow className="action-row">
              <div className="body2">
                Want to log in with a different email address? Add another login
                method.
              </div>
              <ActionButton
                className="edit-button"
                onClick={handleClickAddAuthenticationMethod}
                type={ActionButtonType.SECONDARY}
              >
                Add
              </ActionButton>
            </UserSettingsRow>
          )}
        </UserSettingsSection>
      </div>
      <div className="user-settings-footer">
        <div className="user-settings-footer-row">
          <InteractiveElement
            className="link1 cursor-pointer"
            onClick={() =>
              setCurrentModalType(UserSettingsModalType.DELETE_ACCOUNT)
            }
          >
            Delete account
          </InteractiveElement>
        </div>
      </div>
      <SettingsModal
        appElement={document.getElementById('UserSettingsPage')}
        modalProps={{
          closeModal: () => setCurrentModalType(null),
          currentUserId,
          currentUserNameDisplay: updatedCurrentUserNameDisplay,
          currentEmail: primaryEmail,
          validEmailOptions: availableEmails,
          phoneNumber,
          setCurrentUserNameDisplay: setUpdatedCurrentUserNameDisplay,
          setModalType: (modalType) => setCurrentModalType(modalType),
          setPhoneNumber,
          setPrimaryEmail,
          setToastInfo,
          planExpiresOn: formatMonthDayAndYear(currentUser.planExpiresOn),
          hasActivePlan: currentUser.hasActivePlan || false,
          showPlanSection,
        }}
        modalType={currentModalType}
      />
      {toastInfo.show && (
        <Toast
          text={toastInfo.text}
          color={toastInfo.error ? 'red' : 'yellow'}
          showCloseIcon={toastInfo.error}
          dismissAfterMs={toastInfo.error ? 6000 : 3000}
        />
      )}
      {userDataError && (
        <Toast
          text="Could not load user data. Please refresh the page."
          color="red"
          noAutoDismiss
          showCloseIcon
        />
      )}
    </div>
  )
}
